// src/utils/userService.js
import { supabase } from '../supabaseClient';

// ─── STATS (score, streak, badges etc.) ──────────────────────────────────────

export const loadUserStats = async (userId) => {
  const { data, error } = await supabase
    .from('user_stats')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error && error.code === 'PGRST116') {
    // Row doesn't exist yet — create it and return defaults
    const defaults = { user_id: userId };
    const { error: insertError } = await supabase.from('user_stats').insert([defaults]);
    if (insertError) throw insertError;
    return defaults;
  }
  if (error) throw error;
  return data;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const updateUserStats = async (userId, updates) => {
  if ('email' in updates && updates.email && !EMAIL_REGEX.test(updates.email)) {
    throw new Error('Invalid email format');
  }
  const { error } = await supabase
    .from('user_stats')
    .upsert({ user_id: userId, ...updates, updated_at: new Date().toISOString() });
  if (error) throw error;
};

// ─── LOGS ─────────────────────────────────────────────────────────────────────

export const loadLogs = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_logs')
    .select('*')
    .eq('user_id', userId)
    .order('timestamp', { ascending: false });
  if (error) throw error;
  return (data || []).map(row => ({
    id: row.id,
    challenge: row.challenge,
    points: row.points,
    earnedPoints: row.earned_points,
    category: row.category,
    success: row.success,
    outcome: row.outcome,
    pendingOutcome: row.pending_outcome,
    recoveryBonus: row.recovery_bonus,
    isWeekly: row.is_weekly,
    autoBitched: row.auto_bitched,
    timestamp: row.timestamp,
    date: row.date,
  }));
};

const mapLogToRow = (userId, log, pendingOutcomes = []) => ({
    id: log.id,
    user_id: userId,
    challenge: log.challenge,
    points: log.points,
    earned_points: log.earnedPoints,
    category: log.category,
    success: log.success,
    outcome: log.outcome || null,
    recovery_bonus: log.recoveryBonus || false,
    is_weekly: log.isWeekly || false,
    auto_bitched: log.autoBitched || false,
    pending_outcome: pendingOutcomes.includes(log.id),
    timestamp: log.timestamp,
    date: log.date,
});

export const saveLogs = async (userId, logs, pendingOutcomes = []) => {
  if (logs.length === 0) return;
  const { error } = await supabase
    .from('challenge_logs')
    .upsert(logs.map(log => mapLogToRow(userId, log, pendingOutcomes)), { onConflict: 'id' });
  if (error) throw error;
};

export const insertLog = async (userId, log, pendingOutcomes = []) => {
  const { error } = await supabase
    .from('challenge_logs')
    .upsert([mapLogToRow(userId, log, pendingOutcomes)], { onConflict: 'id' });
  if (error) throw error;
};

// ─── DECISIONS ────────────────────────────────────────────────────────────────

export const loadDecisions = async (userId) => {
  const { data, error } = await supabase
    .from('decisions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(d => ({ id: d.id, text: d.text, savedAt: d.saved_at }));
};

const mapDecisionToRow = (userId, decision) => ({
    id: decision.id,
    user_id: userId,
    text: decision.text,
    saved_at: decision.savedAt,
});

export const saveDecisions = async (userId, decisions) => {
  if (decisions.length === 0) return;
  const { error } = await supabase
    .from('decisions')
    .upsert(decisions.map(decision => mapDecisionToRow(userId, decision)), { onConflict: 'id' });
  if (error) throw error;
};

export const insertDecision = async (userId, decision) => {
  const { error } = await supabase
    .from('decisions')
    .upsert([mapDecisionToRow(userId, decision)], { onConflict: 'id' });
  if (error) throw error;
};

// ─── CHALLENGE STATE ──────────────────────────────────────────────────────────

export const loadChallengeState = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_state')
    .select('*')
    .eq('user_id', userId)
    .single();
  if (error && error.code === 'PGRST116') return null; // No state yet
  if (error) throw error;
  return data;
};

export const saveChallengeState = async (userId, state) => {
  const { error } = await supabase.from('challenge_state').upsert({
    user_id: userId,
    ...state,
    updated_at: new Date().toISOString()
  });
  if (error) throw error;
};

// ─── CUSTOM DAILY CHALLENGE ───────────────────────────────────────────────────

export const saveCustomChallenge = async (userId, challenge) => {
  const { error } = await supabase.from('challenge_state').upsert({
    user_id: userId,
    custom_challenge: challenge,
    updated_at: new Date().toISOString(),
  });
  if (error) throw error;
};

// Reads and atomically clears the custom_challenge so it only fires once.
// Returns the challenge object, or null if none pending.
export const loadAndClearCustomChallenge = async (userId) => {
  const { data, error } = await supabase
    .from('challenge_state')
    .select('custom_challenge')
    .eq('user_id', userId)
    .single();

  if (error && error.code === 'PGRST116') return null;
  if (error) throw error;
  if (!data?.custom_challenge) return null;

  const challenge = data.custom_challenge;

  // Clear it immediately so it can't be loaded twice
  const { error: clearError } = await supabase
    .from('challenge_state')
    .update({ custom_challenge: null, updated_at: new Date().toISOString() })
    .eq('user_id', userId);
  if (clearError) throw clearError;

  return challenge;
};